import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { visitsAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { cn } from '../../lib/utils';
import {
    QrCode, Star, MapPin, Users, ThumbsUp, ThumbsDown, HelpCircle,
    Copy, Check, Loader2, Clock, ChevronDown, ChevronUp, HandCoins,
    Flame, Snowflake, Sun, CreditCard, TrendingUp, Phone, Mail, PenLine
} from 'lucide-react';
import { SendVisitEmailModal } from './SendVisitEmailModal';
import { ManualVisitReportModal } from './ManualVisitReportModal';
import { BonVisiteSection } from './BonVisiteSection';

const getFrontendUrl = () => {
    if (typeof window !== 'undefined') return window.location.origin;
    return process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || '';
};

function StarDisplay({ value, max = 5 }) {
    return (
        <div className="flex gap-0.5">
            {Array.from({ length: max }).map((_, i) => (
                <Star key={i} className={cn("w-3.5 h-3.5", i < value ? "text-yellow-400 fill-yellow-400" : "text-white/10")} />
            ))}
        </div>
    );
}

const choiceLabel = (val) => {
    const map = {
        oui: 'Oui', non: 'Non', partiellement: 'Moyen',
        court_terme: '< 3 mois', '6_mois': '3-6 mois', je_regarde: 'Je regarde',
        je_reflechis: "J'y réfléchis",
        en_cours: 'En cours',
    };
    return map[val] || val;
};

const choiceColor = (val) => {
    if (['oui', 'court_terme'].includes(val)) return 'text-green-400';
    if (['non'].includes(val)) return 'text-red-400';
    return 'text-yellow-400';
};

const TEMP_CONFIG = {
    hot: { icon: Flame, label: 'Chaud', color: 'text-orange-400', bg: 'bg-orange-500/15', border: 'border-orange-500/30' },
    warm: { icon: Sun, label: 'Tiède', color: 'text-yellow-400', bg: 'bg-yellow-500/15', border: 'border-yellow-500/30' },
    cold: { icon: Snowflake, label: 'Froid', color: 'text-blue-400', bg: 'bg-blue-500/15', border: 'border-blue-500/30' },
};

function LeadBadge({ temperature }) {
    const config = TEMP_CONFIG[temperature] || TEMP_CONFIG.cold;
    const Icon = config.icon;
    return (
        <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold", config.bg, config.color, config.border, "border")}
              data-testid={`lead-badge-${temperature}`}>
            <Icon className="w-3 h-3" />
            {config.label}
        </span>
    );
}

export function VisitReportsPanel({ mandateId, propertyAddress, isDark = true, isSeller = false }) {
    const [links, setLinks] = useState([]);
    const [reports, setReports] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [activeLink, setActiveLink] = useState(null);
    const [copied, setCopied] = useState(false);
    const [expanded, setExpanded] = useState(true);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [manualModalOpen, setManualModalOpen] = useState(false);

    const loadData = async () => {
        try {
            const reportsRes = await visitsAPI.getReports(mandateId);
            setReports(reportsRes.data.reports || []);
            setStats(reportsRes.data.stats || null);
            if (!isSeller) {
                const linksRes = await visitsAPI.getLinks(mandateId);
                setLinks(linksRes.data.links || []);
                if (linksRes.data.links?.length > 0) {
                    setActiveLink(linksRes.data.links[0]);
                }
            }
        } catch {
            /* noop */
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/set-state-in-effect
    useEffect(() => { loadData(); }, [mandateId]);

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const res = await visitsAPI.generateLink(mandateId, propertyAddress);
            const newToken = res.data.token;
            setActiveLink({ token: newToken });
            await loadData();
        } catch {
            /* noop */
        } finally {
            setGenerating(false);
        }
    };

    const visitUrl = activeLink ? `${getFrontendUrl()}/visite/${activeLink.token}` : '';

    const handleCopy = () => {
        navigator.clipboard.writeText(visitUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (loading) {
        return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
    }

    const hasLeadTemp = stats?.lead_temperature && (stats.lead_temperature.hot > 0 || stats.lead_temperature.warm > 0 || stats.lead_temperature.cold > 0);

    return (
        <div className="space-y-4">
            {/* Header */}
            <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between" data-testid="visit-reports-toggle">
                <div className="flex items-center gap-2">
                    <QrCode className="w-5 h-5 text-[#FF914D]" />
                    <h3 className="font-bold text-sm">Comptes rendus de visite</h3>
                    {stats && stats.total_visits > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-[#FF914D]/10 text-[#FF914D] text-[10px] font-bold">
                            {stats.total_visits} avis
                        </span>
                    )}
                    {/* Hot leads indicator */}
                    {stats?.lead_temperature?.hot > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-400 text-[10px] font-bold flex items-center gap-1">
                            <Flame className="w-3 h-3" />
                            {stats.lead_temperature.hot}
                        </span>
                    )}
                </div>
                {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </button>

            {expanded && (
                <>
                    {/* QR Code Section (Agent only) */}
                    {!isSeller && (
                    <Card className={cn("p-4 border", isDark ? "bg-white/5 border-white/10" : "bg-slate-50 border-slate-200")}>
                        {activeLink ? (
                            <div className="flex flex-col sm:flex-row items-center gap-4">
                                <div className="bg-white p-3 rounded-xl">
                                    <QRCodeSVG value={visitUrl} size={120} level="M" includeMargin={false} />
                                </div>
                                <div className="flex-1 text-center sm:text-left">
                                    <p className="font-semibold text-sm mb-1">QR Code de visite</p>
                                    <p className="text-xs text-muted-foreground mb-3">
                                        Imprimez-le ou montrez-le aux visiteurs. Ils scannent et remplissent le questionnaire sur leur téléphone.
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <code className={cn("flex-1 text-[10px] px-2 py-1.5 rounded-lg truncate", isDark ? "bg-white/5 text-white/50" : "bg-slate-100 text-slate-500")}>
                                            {visitUrl}
                                        </code>
                                        <Button size="sm" variant="outline" onClick={handleCopy} className="h-8 px-3 shrink-0" data-testid="copy-visit-link">
                                            {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-4">
                                <QrCode className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                                <p className="text-sm text-muted-foreground mb-3">Générez un QR code pour recueillir les avis des visiteurs</p>
                                <Button onClick={handleGenerate} disabled={generating}
                                    className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold" data-testid="generate-qr-btn">
                                    {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <QrCode className="w-4 h-4 mr-2" />}
                                    Générer le QR code
                                </Button>
                            </div>
                        )}
                    </Card>
                    )}

                    {/* Actions agent : email + saisie manuelle */}
                    {!isSeller && (
                        <div className="grid grid-cols-2 gap-2">
                            <Button variant="outline" size="sm" onClick={() => setEmailModalOpen(true)}
                                className="border-white/10 text-white/80 hover:bg-white/5" data-testid="send-visit-email-btn">
                                <Mail className="w-4 h-4 mr-1.5 text-[#FF914D]" />
                                Envoyer par email
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setManualModalOpen(true)}
                                className="border-white/10 text-white/80 hover:bg-white/5" data-testid="manual-report-btn">
                                <PenLine className="w-4 h-4 mr-1.5 text-[#FF914D]" />
                                Saisir manuellement
                            </Button>
                        </div>
                    )}
                    {stats && stats.total_visits > 0 && (
                        <div className="space-y-3">
                            {/* Main KPIs row */}
                            <div className="grid grid-cols-3 gap-3">
                                <Card className={cn("p-3 text-center", isDark ? "bg-white/5 border-white/10" : "bg-slate-50 border-slate-200")} data-testid="stat-avg-rating">
                                    <p className="text-2xl font-bold">{stats.avg_overall}</p>
                                    <div className="flex justify-center my-1"><StarDisplay value={Math.round(stats.avg_overall)} /></div>
                                    <p className="text-[10px] text-muted-foreground">Note moyenne</p>
                                </Card>
                                <Card className={cn("p-3 text-center", isDark ? "bg-white/5 border-white/10" : "bg-slate-50 border-slate-200")} data-testid="stat-interested">
                                    <p className="text-2xl font-bold text-green-400">{stats.interested_count}</p>
                                    <ThumbsUp className="w-4 h-4 text-green-400 mx-auto my-1" />
                                    <p className="text-[10px] text-muted-foreground">Intéressés</p>
                                </Card>
                                <Card className={cn("p-3 text-center", isDark ? "bg-white/5 border-white/10" : "bg-slate-50 border-slate-200")} data-testid="stat-offers">
                                    <p className="text-2xl font-bold text-[#FF914D]">{stats.offer_count}</p>
                                    <HandCoins className="w-4 h-4 text-[#FF914D] mx-auto my-1" />
                                    <p className="text-[10px] text-muted-foreground">Veulent offrir</p>
                                </Card>
                            </div>

                            {/* Lead temperature bar (Agent view only) */}
                            {!isSeller && hasLeadTemp && (
                                <Card className={cn("p-3 border", isDark ? "bg-white/5 border-white/10" : "bg-slate-50 border-slate-200")} data-testid="lead-temperature-bar">
                                    <p className="text-[10px] text-muted-foreground mb-2 font-semibold uppercase tracking-wider">Température des leads</p>
                                    <div className="flex h-3 rounded-full overflow-hidden bg-white/5">
                                        {stats.lead_temperature.hot > 0 && (
                                            <div className="bg-orange-500 transition-all" style={{ width: `${(stats.lead_temperature.hot / stats.total_visits) * 100}%` }} />
                                        )}
                                        {stats.lead_temperature.warm > 0 && (
                                            <div className="bg-yellow-500 transition-all" style={{ width: `${(stats.lead_temperature.warm / stats.total_visits) * 100}%` }} />
                                        )}
                                        {stats.lead_temperature.cold > 0 && (
                                            <div className="bg-blue-500 transition-all" style={{ width: `${(stats.lead_temperature.cold / stats.total_visits) * 100}%` }} />
                                        )}
                                    </div>
                                    <div className="flex justify-between mt-2 text-[10px]">
                                        <span className="flex items-center gap-1 text-orange-400"><Flame className="w-3 h-3" /> {stats.lead_temperature.hot} chaud{stats.lead_temperature.hot > 1 ? 's' : ''}</span>
                                        <span className="flex items-center gap-1 text-yellow-400"><Sun className="w-3 h-3" /> {stats.lead_temperature.warm} tiède{stats.lead_temperature.warm > 1 ? 's' : ''}</span>
                                        <span className="flex items-center gap-1 text-blue-400"><Snowflake className="w-3 h-3" /> {stats.lead_temperature.cold} froid{stats.lead_temperature.cold > 1 ? 's' : ''}</span>
                                    </div>
                                </Card>
                            )}

                            {/* Financing & Budget summary (Agent view only) */}
                            {!isSeller && (stats.financing_ready > 0 || stats.financing_pending > 0) && (
                                <Card className={cn("p-3 border", isDark ? "bg-white/5 border-white/10" : "bg-slate-50 border-slate-200")} data-testid="stat-financing">
                                    <div className="flex items-center gap-1.5 mb-1">
                                        <CreditCard className="w-3.5 h-3.5 text-green-400" />
                                        <p className="text-[10px] text-muted-foreground font-semibold">Financement</p>
                                    </div>
                                    <p className="text-sm">
                                        <span className="text-green-400 font-bold">{stats.financing_ready}</span>
                                        <span className="text-muted-foreground"> validé{stats.financing_ready > 1 ? 's' : ''}</span>
                                        {stats.financing_pending > 0 && (
                                            <span className="text-yellow-400 ml-1">+ {stats.financing_pending} en cours</span>
                                        )}
                                    </p>
                                </Card>
                            )}

                            {/* Seller-specific summary */}
                            {isSeller && stats.total_visits > 0 && (
                                <Card className={cn("p-4 border", isDark ? "bg-[#681793]/5 border-[#681793]/20" : "bg-purple-50 border-purple-200")} data-testid="seller-summary">
                                    <div className="flex items-start gap-3">
                                        <TrendingUp className="w-5 h-5 text-[#FF914D] mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="text-sm font-semibold text-white mb-1">Synthèse des visites</p>
                                            <p className="text-xs text-muted-foreground leading-relaxed">
                                                {stats.total_visits} visiteur{stats.total_visits > 1 ? 's' : ''} {stats.total_visits > 1 ? 'ont' : 'a'} donné leur avis.
                                                {stats.offer_count > 0 && (<> <strong className="text-green-400">{stats.offer_count}</strong> souhaitent faire une offre.</>)}
                                                {stats.offer_count === 0 && stats.interested_count > 0 && (<> <strong className="text-yellow-400">{stats.interested_count}</strong> se montrent intéressés.</>)}
                                                {stats.avg_overall >= 4 && <> Note moyenne excellente de <strong className="text-yellow-400">{stats.avg_overall}/5</strong>.</>}
                                                {stats.avg_overall >= 3 && stats.avg_overall < 4 && <> Note moyenne correcte de {stats.avg_overall}/5.</>}
                                                {stats.avg_overall < 3 && <> Note moyenne de {stats.avg_overall}/5, des ajustements pourraient aider.</>}
                                            </p>
                                        </div>
                                    </div>
                                </Card>
                            )}
                        </div>
                    )}

                    {/* Reports List */}
                    {reports.length > 0 && (
                        <div className="space-y-2">
                            {reports.map(r => (
                                <Card key={r.id} className={cn("p-3 border", isDark ? "bg-white/[0.03] border-white/5" : "bg-white border-slate-200")} data-testid={`visit-report-${r.id}`}>
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <div>
                                                <p className="font-semibold text-sm">{r.visitor_first_name} {r.visitor_last_name}</p>
                                                <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    {new Date(r.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                </p>
                                            </div>
                                            {!isSeller && r.lead_temperature && <LeadBadge temperature={r.lead_temperature} />}
                                        </div>
                                        <StarDisplay value={r.rating_overall} />
                                    </div>

                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                        <div>
                                            <span className="text-muted-foreground">Luminosité</span>
                                            <StarDisplay value={r.rating_luminosity} />
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">État</span>
                                            <StarDisplay value={r.rating_condition} />
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">Emplacement</span>
                                            <p className={cn("font-medium", choiceColor(r.location_ok))}>{choiceLabel(r.location_ok)}</p>
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">Prix</span>
                                            <p className={cn("font-medium", choiceColor(r.price_ok))}>{choiceLabel(r.price_ok)}</p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                                        <div>
                                            <span className="text-muted-foreground">Délai achat</span>
                                            <p className={cn("font-medium", choiceColor(r.purchase_timeline))}>{choiceLabel(r.purchase_timeline)}</p>
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">Offre ?</span>
                                            <p className={cn("font-medium", choiceColor(r.would_make_offer))}>{choiceLabel(r.would_make_offer)}</p>
                                        </div>
                                    </div>

                                    {/* Financing & Budget (Agent view only) */}
                                    {/* Financing info (Agent only) */}
                                    {!isSeller && r.has_financing && (
                                        <div className="mt-2 text-xs">
                                            <span className="text-muted-foreground flex items-center gap-1"><CreditCard className="w-3 h-3" /> Financement</span>
                                            <p className={cn("font-medium", r.has_financing === 'oui' ? 'text-green-400' : r.has_financing === 'en_cours' ? 'text-yellow-400' : 'text-white/50')}>
                                                {choiceLabel(r.has_financing)}
                                            </p>
                                        </div>
                                    )}

                                    {/* Contact info for agents on hot/warm leads */}
                                    {!isSeller && r.lead_temperature === 'hot' && r.visitor_phone && (
                                        <a href={`tel:${r.visitor_phone}`}
                                           className="mt-2 flex items-center gap-2 p-2 rounded-lg bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-medium hover:bg-orange-500/20 transition-colors"
                                           data-testid={`call-lead-${r.id}`}>
                                            <Phone className="w-3.5 h-3.5" />
                                            Appeler {r.visitor_first_name} ({r.visitor_phone})
                                        </a>
                                    )}

                                    {r.comment && (
                                        <div className={cn("mt-2 p-2 rounded-lg text-xs italic", isDark ? "bg-white/5 text-white/60" : "bg-slate-50 text-slate-600")}>
                                            &laquo;&nbsp;{r.comment}&nbsp;&raquo;
                                        </div>
                                    )}
                                </Card>
                            ))}
                        </div>
                    )}

                    {reports.length === 0 && stats?.total_visits === 0 && (activeLink || isSeller) && (
                        <div className="text-center py-6" data-testid="visit-empty-state">
                            <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
                            <p className="text-muted-foreground text-sm mb-1 font-medium">
                                {isSeller ? "Aucun retour de visite pour le moment" : "Aucun compte rendu pour le moment"}
                            </p>
                            <p className="text-muted-foreground/60 text-xs">
                                {isSeller
                                    ? "Vous serez notifié dès que des visiteurs auront donné leur avis sur votre bien."
                                    : "Partagez le QR code lors de vos visites pour recueillir les avis en temps réel."}
                            </p>
                        </div>
                    )}

                    {/* Bons de visite acquéreur (agent uniquement) */}
                    {!isSeller && <BonVisiteSection mandateId={mandateId} />}
                </>
            )}

            {/* Modales agent */}
            {!isSeller && (
                <>
                    <SendVisitEmailModal open={emailModalOpen} onClose={() => setEmailModalOpen(false)}
                        mandateId={mandateId} propertyAddress={propertyAddress} />
                    <ManualVisitReportModal open={manualModalOpen} onClose={() => setManualModalOpen(false)}
                        mandateId={mandateId} onSaved={loadData} />
                </>
            )}
        </div>
    );
}
